<template>
  <div class="w-[400px] mx-auto">
    <a-form
      :model="formState"
      v-bind="layout === 'vertical' ? formLayoutVertical : formLayout"
      name="basic"
      autocomplete="off"
      hide-required-mark
      class="mt-[50px] w-full"
      @finish="onFinish"
      @validate="onValidate"
    >
      <a-form-item
        v-if="!isGlobalVersion()"
        name="mobile"
        :rules="[
          {
            required: true,
            validator: validatePhone,
            trigger: 'blur',
          },
        ]"
      >
        <a-input
          v-model:value="formState.mobile"
          :class="{
            'yy-input-xlg': xLarge,
          }"
          size="large"
          :placeholder="$t('shou-ji-hao')"
        />
      </a-form-item>
      <a-form-item
        v-else
        name="email"
        :rules="[
          {
            required: true,
            message: $t('qing-shu-ru-you-xiang'),
            trigger: 'blur',
          },
          {
            pattern: emailReg,
            message: $t('qing-shu-ru-zheng-que-de-you-xiang-ge-shi'),
            trigger: 'blur',
          },
        ]"
      >
        <a-input
          v-model:value="formState.email"
          :class="{
            'yy-input-xlg': xLarge,
          }"
          :placeholder="$t('you-xiang')"
        />
      </a-form-item>
      <a-form-item
        name="sms_code"
        :rules="[
          {
            required: true,
            message: $t('qing-shu-ru-yan-zheng-ma'),
            trigger: 'blur',
          },
          {
            pattern: smsCodeReg,
            message: $t('6-wei-shu-zi'),
            trigger: 'blur',
          },
        ]"
      >
        <XCaptchaInput
          v-model:value="formState.sms_code"
          :placeholder="
            isGlobalVersion()
              ? $t('you-xiang-yan-zheng-ma')
              : $t('shou-ji-yan-zheng-ma')
          "
          :data="isGlobalVersion() ? formState.email : formState.mobile"
          :size="xLarge ? undefined : 'large'"
          :x-large="xLarge"
          :type="isGlobalVersion() && !userInfo?.user_id ? 'primary' : 'link'"
        />
      </a-form-item>
      <a-form-item
        name="password"
        auto-complete="off"
        :help="false"
        :rules="[
          {
            validator: PwdValidator.validateNewPassword,
            trigger: ['change', 'blur'],
          },
        ]"
      >
        <a-input-password
          v-model:value="formState.password"
          class="mb-[5px]"
          size="large"
          :placeholder="$t('xin-mi-ma')"
        />
        <div
          :class="{
            'pwd-rule-tip': true,
            success: PwdValidator.isSafeRange(formState.password),
          }"
        >
          <iconpark-icon name="circle-check"></iconpark-icon>
          {{ $t('chang-du-wei-620-ge-zi-fu') }}
        </div>
        <div
          :class="{
            'pwd-rule-tip': true,
            success: PwdValidator.isValidFormat(formState.password),
          }"
        >
          <iconpark-icon name="circle-check"></iconpark-icon>
          {{
            $t(
              'bao-han-shu-zi-da-xie-zi-mu-xiao-xie-zi-fu-zi-fu-san-zhong-ji-yi-shang'
            )
          }}
        </div>
      </a-form-item>
      <a-form-item
        name="checkPassword"
        auto-complete="off"
        :rules="[
          {
            required: true,
            validator: (_rule, value) =>
              validatePassword(_rule, value, formState.password),
            trigger: ['blur', 'change'],
          },
        ]"
      >
        <a-input-password
          v-model:value="formState.checkPassword"
          size="large"
          :placeholder="$t('que-ren-mi-ma')"
          auto-complete="new-password"
        />
      </a-form-item>
      <a-form-item :wrapper-col="{ offset: 0, span: 24 }">
        <a-button
          :size="xLarge ? undefined : 'large'"
          type="primary"
          html-type="submit"
          class="w-full mt-[30px]"
          :class="{
            'yy-btn-xlg': xLarge,
          }"
        >
          {{ $t('que-ren') }}
        </a-button>
      </a-form-item>
    </a-form>
  </div>
</template>

<script lang="ts" setup>
import { JSEncrypt } from 'jsencrypt/lib'
import {
  changePassword,
  getPublicKey,
  changePasswordLogin,
  changePasswordEmail,
} from '@/api'
import type { PublicKeyData } from '~/api/interface'
import { PwdValidator } from '~/utils/form'

const { $t } = useNuxtApp()
interface FormState {
  mobile: string
  sms_code: string
  password: string
  checkPassword: string
  email: string
}

interface IPublicKeyPayload {
  password2: string
  password1: string
}

const userInfo = useUserInfo()

const formLayout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 24 },
}

const formLayoutVertical = {
  labelCol: { span: 6 },
  wrapperCol: { span: 24 },
}

defineProps<{
  noLabel?: boolean
  size?: string
  xLarge?: boolean
  layout?: 'horizontal' | 'vertical' | 'inline'
}>()

const emits = defineEmits(['cancel', 'success'])

const formState = reactive<FormState>({
  mobile: userInfo.value?.phone_number || '',
  sms_code: '',
  password: '',
  checkPassword: '',
  email: userInfo.value?.email_address || '',
})

onBeforeUnmount(() => {
  formState.mobile = ''
  formState.sms_code = ''
  formState.password = ''
  formState.checkPassword = ''
})

const clearData = () => {
  formState.sms_code = ''
  formState.password = ''
  formState.checkPassword = ''
}

const showTip = ref(true)
const onValidate = (
  name: string | number | string[] | number[],
  status: boolean,
  msg: string[]
) => {
  if (name === 'password') {
    showTip.value = !msg ? false : status
  }
}

// 修改密码
const onFinish = async (values: any) => {
  const checkParams: any = isGlobalVersion()
    ? {
        email: values.email,
        password: values.password,
        verify_code: values.sms_code,
      }
    : { password2: values.checkPassword, password1: values.password }
  const [encrypted, rsa] = await transformPublicKey(checkParams)

  const action = isGlobalVersion()
    ? changePasswordEmail
    : userInfo.value?.user_id
      ? changePasswordLogin
      : changePassword

  const params: any = isGlobalVersion()
    ? {
        rsa_key_id: rsa.id,
        auth_data: encrypted,
      }
    : {
        mobile: values.mobile,
        sms_code: values.sms_code,
        rsa_key_id: rsa.id,
        auth_data: encrypted,
      }

  await action(params)

  clearData()
  message.success($t('xiu-gai-mi-ma-cheng-gong'))
  trackEvent(TRACK_EVENT.PASSWORD_CHANGE_SUCCESS)
  // 登录后修改
  if (userInfo.value?.user_id) {
    emits('cancel')
  } else {
    emits('success')
    // showAccountType.value = 'login'
  }
}
</script>

<style lang="scss" scoped>
.pwd-rule-tip {
  display: flex;
  align-items: center;
  margin-top: 2px;
  iconpark-icon {
    color: #cacdda;
    margin-right: 5px;
  }
  &.success iconpark-icon {
    color: #29e69c;
  }
}
</style>
